import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import { addTime, subtractTime, toDate, toDateString } from '@/utils/date';

import type { LiceReportMode } from './LiceReportModeToggle';
import WeekChips, { type WeekNumberObject } from './WeekChips';

import styles from './LiceReportControls.module.scss';

export type LiceReportDailySubMode = 'single' | 'range';

export interface LiceReportConfiguration {
    from?: string;
    to?: string;
    weekNumbers?: WeekNumberObject[];
    dataSourceType?: 'reporting' | 'raw';
}

export interface LiceReportFiltersProps {
    mode: LiceReportMode;
    /** Weekly: the weeks the reader can pick from, newest first. */
    weekNumberObjects: WeekNumberObject[];
    /** Week to select until the reader picks one. Omit to start on the newest week. */
    defaultWeekKey?: string;
    /** Passed to the week chips so a site or client change drops a manual week. */
    selectionScope?: string;
    onReportConfigurationChange: (config: LiceReportConfiguration) => void;
    dataSourceType?: 'raw' | 'reporting';
    showDataSourceToggle?: boolean;
    dailySubMode?: LiceReportDailySubMode;
    onDailySubModeChange?: (mode: LiceReportDailySubMode) => void;
    selectedDate?: string;
    onDateChange?: (date: string) => void;
    rangeFrom?: string;
    rangeTo?: string;
    onRangeChange?: (range: DateRange) => void;
    /** Kept on screen but not interactive, so the row can paint before the report arrives. */
    disabled?: boolean;
}

/**
 * The controls that narrow the report within the mode the reader has chosen.
 *
 * In the page body, above the table, rather than in the title row: the weekly mode shows a
 * wrapping row of week pills, which level with the heading pushed the layout around. Which set
 * is drawn follows the mode, so only one is ever on screen.
 */
export default function LiceReportFilters(props: LiceReportFiltersProps) {
    const {
        mode,
        weekNumberObjects,
        defaultWeekKey,
        selectionScope,
        onReportConfigurationChange,
        dataSourceType = 'reporting',
        showDataSourceToggle = false,
        dailySubMode = 'single',
        onDailySubModeChange,
        selectedDate,
        onDateChange,
        rangeFrom,
        rangeTo,
        onRangeChange,
        disabled = false,
    } = props;
    const { t } = useTranslation('liceReport');
    const { t: tGlobal } = useTranslation();

    const [reportDateRange] = useState<{ from: string; to: string }>({
        from: toDateString(subtractTime(new Date(), 7, 'day')),
        to: toDateString(new Date()),
    });
    const [weekNumbers, setWeekNumbers] = useState<WeekNumberObject[]>([]);

    useEffect(() => {
        // Chips publish an empty list before the weeks arrive. Treating that as "select the
        // newest week" opened a location on a week that site does not have.
        if (mode === 'weekly' && weekNumbers.length === 0) {
            return;
        }
        onReportConfigurationChange({ ...reportDateRange, weekNumbers: [...weekNumbers] });
    }, [mode, onReportConfigurationChange, reportDateRange, weekNumbers]);

    const selectedDateValue = toDate(selectedDate) ?? new Date();
    const todayString = toDateString(new Date());
    const canGoNextDay = toDateString(selectedDateValue) < todayString;

    const handleDayOffset = (offset: number) => {
        const nextDate = addTime(selectedDateValue, offset, 'day');
        if (!nextDate) {
            return;
        }
        const nextDateString = toDateString(nextDate);
        if (nextDateString > todayString) {
            return;
        }
        onDateChange?.(nextDateString);
    };

    const handlePickedDay = (date: Date | null) => {
        // The field sends null for every partial, invalid or future entry, once per key, so a
        // null means the reader is not done typing rather than a pick. Leaving the field puts
        // the report's day back in it.
        if (date) {
            onDateChange?.(toDateString(date));
        }
    };

    if (mode === 'weekly') {
        return (
            <fieldset
                className={styles.filters}
                aria-busy={disabled || undefined}
                aria-label={tGlobal('common.labels.week')}>
                <WeekChips
                    onChange={(newWeeks) => setWeekNumbers([...newWeeks])}
                    weeks={weekNumberObjects}
                    defaultWeekKey={defaultWeekKey}
                    selectionScope={selectionScope}
                    disabled={disabled}
                />
            </fieldset>
        );
    }

    return (
        <div className={styles.filters}>
            <SegmentedControl<LiceReportDailySubMode>
                value={dailySubMode}
                onChange={(subMode) => onDailySubModeChange?.(subMode)}
                options={[
                    { value: 'single', label: t('filters.singleDay') },
                    { value: 'range', label: t('filters.range') },
                ]}
            />

            {dailySubMode === 'single' ? (
                <div className={styles.datePickerContainer}>
                    <Button
                        variant="secondary"
                        type="button"
                        aria-label={t('filters.previousDay')}
                        onClick={() => handleDayOffset(-1)}
                        iconOnly>
                        {'<'}
                    </Button>
                    <StyledSingleDatePicker
                        date={selectedDateValue}
                        blockFutureDates={true}
                        onDateChange={handlePickedDay}
                    />
                    <Button
                        variant="secondary"
                        type="button"
                        aria-label={t('filters.nextDay')}
                        disabled={!canGoNextDay}
                        onClick={() => handleDayOffset(1)}
                        iconOnly>
                        {'>'}
                    </Button>
                </div>
            ) : (
                <DateRangePicker
                    dateFrom={rangeFrom}
                    dateTo={rangeTo}
                    defaultPresetId="last7days"
                    menuPosition="right"
                    onDateChange={onRangeChange}
                />
            )}

            {showDataSourceToggle && (
                <SegmentedControl<'raw' | 'reporting'>
                    className={styles.dataSourceSwitch}
                    defaultValue={dataSourceType}
                    onChange={(value) =>
                        onReportConfigurationChange({
                            ...reportDateRange,
                            weekNumbers: [...weekNumbers],
                            dataSourceType: value,
                        })
                    }
                    options={[
                        {
                            value: 'raw',
                            label: tGlobal('common.labels.rawData'),
                            tooltip: tGlobal('common.labels.rawdataexplain'),
                        },
                        {
                            value: 'reporting',
                            label: tGlobal('common.labels.simulatedManualCount'),
                            tooltip: tGlobal('common.labels.reportdataexplain'),
                        },
                    ]}
                />
            )}
        </div>
    );
}
